import React from "react";
import { RefreshCw, Home } from "lucide-react";
import { isChunkLoadError, reloadOnceForChunk, reportClientCrash, markIntentionalReload } from "@/lib/crashReport";

// Filet de sécurité racine : au lieu d'un écran blanc (React démonte tout sur une exception de rendu),
// un écran de secours « Oups » + Recharger, et la pile d'erreur part au serveur (Admin › Supervision).
// Hors AppContext (il l'enveloppe) → mini-dictionnaire local, langue lue dans localStorage.
const TEXTS = {
  fr: { title: "Oups, une erreur est survenue", body: "L'équipe Yalola vient d'en être informée. Recharge l'application pour continuer.", reload: "Recharger l'application", home: "Retour à l'accueil", updating: "Mise à jour de l'application…" },
  en: { title: "Oops, something went wrong", body: "The Yalola team has just been notified. Reload the app to continue.", reload: "Reload the app", home: "Back to home", updating: "Updating the app…" },
  es: { title: "Vaya, ha ocurrido un error", body: "El equipo de Yalola acaba de ser avisado. Recarga la aplicación para continuar.", reload: "Recargar la aplicación", home: "Volver al inicio", updating: "Actualizando la aplicación…" },
  pt: { title: "Ups, ocorreu um erro", body: "A equipa Yalola acabou de ser avisada. Recarrega a aplicação para continuar.", reload: "Recarregar a aplicação", home: "Voltar ao início", updating: "A atualizar a aplicação…" },
};
const texts = () => {
  let lang = "fr";
  try { lang = (localStorage.getItem("yalola.lang") || navigator.language || "fr").slice(0, 2).toLowerCase(); } catch { /* noop */ }
  return TEXTS[lang] || TEXTS.fr;
};

export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, updating: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    import("@/lib/splash").then((m) => m.hideSplash()).catch(() => {}); // lot 702 — l'écran d'erreur doit être visible
    // Morceau de l'app disparu après un déploiement → on recharge une fois, sans alarmer le membre.
    if (isChunkLoadError(error) && reloadOnceForChunk()) {
      this.setState({ updating: true });
      return;
    }
    reportClientCrash({ error, componentStack: info?.componentStack || "", kind: "render" });
  }

  render() {
    const { error, updating } = this.state;
    if (!error) return this.props.children;
    const T = texts();
    if (updating) {
      return (
        <div className="min-h-screen grid place-items-center px-6 text-center" style={{ background: "var(--yalola-bg, #FAF7F2)", color: "var(--yalola-text, #2B2B2B)" }} data-testid="app-error-updating">
          <div className="flex items-center gap-2 text-sm font-semibold"><RefreshCw size={16} className="animate-spin" /> {T.updating}</div>
        </div>
      );
    }
    return (
      <div className="min-h-screen grid place-items-center px-6" style={{ background: "var(--yalola-bg, #FAF7F2)", color: "var(--yalola-text, #2B2B2B)" }} data-testid="app-error-screen">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto h-16 w-16 rounded-full grid place-items-center mb-5" style={{ background: "var(--yalola-primary-soft, #FBE9E1)" }}>
            <span className="text-3xl" aria-hidden>🙏</span>
          </div>
          <h1 className="text-2xl font-bold font-display" data-testid="app-error-title">{T.title}</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--yalola-text-muted, #6B6B6B)" }}>{T.body}</p>
          <button type="button" onClick={() => { markIntentionalReload("error-screen"); window.location.reload(); }} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white" style={{ background: "var(--yalola-primary, #D97757)" }} data-testid="app-error-reload">
            <RefreshCw size={16} /> {T.reload}
          </button>
          <a href="/home" className="mt-3 inline-flex items-center justify-center gap-1.5 text-sm font-semibold" style={{ color: "var(--yalola-primary, #D97757)" }} data-testid="app-error-home">
            <Home size={14} /> {T.home}
          </a>
          {process.env.NODE_ENV !== "production" && (
            <pre className="mt-5 text-left text-[11px] whitespace-pre-wrap rounded-xl p-3 overflow-auto max-h-48" style={{ background: "rgba(0,0,0,0.05)" }} data-testid="app-error-detail">{String(error?.stack || error?.message || error)}</pre>
          )}
        </div>
      </div>
    );
  }
}
